import { api } from './api';

// `daily` conservé en type pour tolérer d'éventuels objectifs historiques
// (non créables via l'UI mais potentiellement retournés par l'API si déjà
// stockés). Les 4 périodes supportées en création sont weekly → yearly.
export type ObjectiveType =
  | 'revenue'
  | 'meetings'
  | 'quotes'
  | 'calls'
  | 'signed_opportunities'
  | 'new_prospects';
export type ObjectivePeriod = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
export type CreatableObjectivePeriod = Exclude<ObjectivePeriod, 'daily'>;

export interface ObjectiveItem {
  id: string;
  type: ObjectiveType;
  targetValue: string;
  periodType: ObjectivePeriod;
  periodStart: string;
  periodEnd: string;
  userId: string | null;
  teamId: string | null;
  createdAt: string;
  user?: { id: string; firstName: string; lastName: string; email: string } | null;
  creator?: { id: string; firstName: string; lastName: string };
}

export interface ObjectiveProgress {
  objectiveId: string;
  target: number;
  achieved: number;
  progressPct: number;
  daysRemaining: number;
}

export interface CreateObjectivePayload {
  type: ObjectiveType;
  targetValue: number;
  periodType: CreatableObjectivePeriod;
  periodStart: string;
  periodEnd: string;
  userId?: string | null;
  teamId?: string | null;
}

export interface BulkCreateObjectivesPayload {
  type: ObjectiveType;
  targetValue: number;
  periodType: CreatableObjectivePeriod;
  periodStart: string;
  periodEnd: string;
  userIds: string[];
}

export interface UpdateObjectivePayload {
  targetValue?: number;
  periodStart?: string;
  periodEnd?: string;
}

function qs(f: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const objectivesApi = {
  list: (token: string, f: { active_only?: boolean } = {}) =>
    api.get<ObjectiveItem[]>(
      `/api/v1/objectives${qs(
        f.active_only === undefined ? {} : { active_only: f.active_only ? 'true' : 'false' },
      )}`,
      { accessToken: token },
    ),
  progress: (token: string, id: string) =>
    api.get<ObjectiveProgress>(`/api/v1/objectives/${id}/progress`, { accessToken: token }),
  create: (token: string, body: CreateObjectivePayload) =>
    api.post<ObjectiveItem>('/api/v1/objectives', body, { accessToken: token }),
  bulkCreate: (token: string, body: BulkCreateObjectivesPayload) =>
    api.post<ObjectiveItem[]>('/api/v1/objectives/bulk', body, { accessToken: token }),
  update: (token: string, id: string, body: UpdateObjectivePayload) =>
    api.patch<ObjectiveItem>(`/api/v1/objectives/${id}`, body, { accessToken: token }),
  delete: (token: string, id: string) =>
    api.delete<void>(`/api/v1/objectives/${id}`, { accessToken: token }),
};

export const OBJECTIVE_TYPE_LABELS: Record<ObjectiveType, string> = {
  revenue: 'CA signé',
  meetings: 'RDV',
  quotes: 'Devis',
  calls: 'Appels',
  signed_opportunities: 'Opportunités signées',
  new_prospects: 'Nouveaux prospects',
};

export const OBJECTIVE_PERIOD_LABELS: Record<ObjectivePeriod, string> = {
  daily: 'Quotidien',
  weekly: 'Hebdomadaire',
  monthly: 'Mensuel',
  quarterly: 'Trimestriel',
  yearly: 'Annuel',
};
